<template>
  <div :class="[$style.modal, $style.itemTableContainer]">
    <div :class="$style.modalContent">
      <ModalHeader
        :handleClose="handleClose"
        :headerText="headerText"
        :showClose="true"
      />
      <div :class="$style.modalBody">
        <table :class="[$style.table, $style.tableWithoutSelectColumn]">
          <thead>
            <tr>
              <th :class="[$style.statusColumn, $style.tableHeadCell]">
                Status
              </th>
              <th :class="$style.tableHeadCell">
                Name
              </th>
              <th :class="$style.tableHeadCell">
                Email
              </th>
              <th :class="$style.tableHeadCell">
                Reason
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="{ user, error } in unremovableUsers"
              :key="user.id"
              :class="$style.tableBodyRow"
            >
              <td :class="[$style.statusColumn, $style.tableBodyCell]">
                <StatusIcon
                  :item="user"
                  :type="BodyComponentConsts.statusIconTypes.systemUser"
                />
              </td>
              <td :class="$style.tableBodyCell">
                <ItemName
                  :item="user"
                  subtitleField="username"
                  :titleValue="getFirstAndLastName(user)"
                />
              </td>
              <td :class="$style.tableBodyCell">
                {{ user.email }}
              </td>
              <td :class="$style.tableBodyCell">
                {{ error.message }}
                <small
                  v-if="isKnownError(error)"
                  :class="$style.errorMessageDetails"
                >
                  <MarkdownInline :mdTemplate="errorMessageDetails(error)" />
                </small>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <ModalFooter
        :handleClose="handleClose"
        :handleMainAction="handleClose"
        mainActionText="OK"
      />
    </div>
  </div>
</template>
<script>
import { BodyComponentConsts, ItemTypeConsts, StoreModuleMap } from 'client/util/Constants';
import { MarkdownInline } from '@jumpcloud/ui-components';
import { mapGetters } from 'vuex';
import ItemName from 'client/components/ItemTable/BodyComponents/ItemName.vue';
import KbArticleUrls from 'client/util/Constants/KbArticleUrls';
import ModalEventBus from 'client/components/Modal/ModalEventBus';
import ModalFooter from 'client/components/Modal/ModalComponents/ModalFooter.vue';
import ModalHeader from 'client/components/Modal/ModalComponents/ModalHeader.vue';
import StatusIcon from 'client/components/ItemTable/BodyComponents/StatusIcon.vue';
import pluralize from 'pluralize';

const storeModule = StoreModuleMap[ItemTypeConsts.systemUser];

export default {
  name: 'UnremovableUsersModal',

  components: {
    ItemName,
    MarkdownInline,
    ModalFooter,
    ModalHeader,
    StatusIcon,
  },

  props: {
    unremovableUsers: {
      type: Array,
      required: true,
      validator(unremovableUsers) {
        if (unremovableUsers.length === 0) {
          return false;
        }

        return unremovableUsers.every(({ user, error }) => typeof user === 'object' && error instanceof Error);
      },
    },
  },

  data() {
    return {
      BodyComponentConsts,
    };
  },

  computed: {
    ...mapGetters(storeModule, {
      getFirstAndLastName: 'getFirstAndLastName',
    }),

    headerText() {
      return `${pluralize('users', this.unremovableUsers.length, true)} couldn't be removed`;
    },
  },

  methods: {
    errorMessageDetails(message) {
      if (this.isManagedByActiveDirectory(message)) {
        const href = KbArticleUrls.ADSyncAgentKB;
        return `In order to make changes to Active Directory you just download our [Sync Agent Installer](${href}).`;
      }

      if (this.isSambaServiceAccount(message)) {
        const href = KbArticleUrls.enablingSambaSupportWithLdap;
        return `This is the active Samba Service Account user. To delete this user you just designate a new account or [disable Samba Authentication](${href}).`;
      }

      return '';
    },

    handleClose() {
      ModalEventBus.close();
    },

    isKnownError(message) {
      return this.isManagedByActiveDirectory(message) || this.isSambaServiceAccount(message);
    },

    isManagedByActiveDirectory(message) {
      return !!String(message).toLowerCase().match('active directory');
    },

    isSambaServiceAccount(message) {
      return !!String(message).toLowerCase().match('samba');
    },
  },
};
</script>
<style scoped module>
@import './css/ModalStyles.css';
@import '../ItemTable/css/ItemTable.css';
@import '../ItemTable/css/itemTableContainers.css';

.modalBody {
  padding: 0 10px;
}

.errorMessageDetails {
  display: block;
}
</style>
